"use client";

import { useEffect, useMemo, useRef } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { setRendezvousReminder } from "../redux/slices/rendezvousSlice";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";
import Cookies from "js-cookie";
const pickUserId = (auth) => {
  const u = auth?.currentUser || auth?.user || {};
  return u.externalId || u.uuid || u._id || u.id || u.userId || null;
};

// Mounted app-wide (both patient and doctor shells), so it can't assume a
// role — pick whichever token actually belongs to the logged-in session
// instead of always reading the patient cookie. `userRole` is set explicitly
// by both login flows, so trust it first; a stale token left over from a
// previous session in the same browser must not win over the active role.
const pickToken = () => {
  const doctorToken =
    Cookies.get("doctorAccessToken") || Cookies.get("doctor_token") || Cookies.get("doctorToken") || null;
  const patientToken = Cookies.get("accessToken") || null;
  const storedRole = typeof window !== "undefined" ? window.localStorage.getItem("userRole") : null;

  if (storedRole === "patient") return patientToken || doctorToken;
  if (storedRole === "doctor") return doctorToken || patientToken;
  return doctorToken || patientToken;
};

// ✅ enlève /api seulement pour socket
const stripApi = (url) => String(url || "").replace(/\/api\/?$/, "");
const SOCKET_DEBUG = import.meta.env.DEV && import.meta.env.VITE_DEBUG_RDV_SOCKET === "true";
const socketLog = (...args) => {
  if (SOCKET_DEBUG) {}
};
const socketWarn = (...args) => {
  if (SOCKET_DEBUG) console.warn(...args);
};

export default function rendezvousSocket() {
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const socketRef = useRef(null);
  const token = pickToken();
  const userId = useMemo(() => effectiveUserId || pickUserId(auth), [effectiveUserId, auth]);

  useEffect(() => {
    socketLog("[useRendezvousSocket] effect start");

    const gwEnv = import.meta.env.VITE_GATEWAY_URL || "https://back.mediwave.tn/api";
    const wsBase = stripApi(gwEnv);

    socketLog("[useRendezvousSocket] base", {
      gwEnv,
      wsBase,
      hasToken: !!token,
      userId,
    });

    if (!token) {
      socketWarn("[useRendezvousSocket] ❌ no token -> skip socket");
      return;
    }

    if (!userId) {
      socketWarn("[useRendezvousSocket] ⚠️ no userId yet -> wait auth load");
      return;
    }

    // éviter double connect
    if (socketRef.current) {
      socketLog("[useRendezvousSocket] already connected -> skip");
      return;
    }

    // ✅ IMPORTANT: namespace /rendezvous (PAS /api)
    const socket = io(`${wsBase}/rendezvous`, {
      path: "/socket.io",
      transports: ["polling", "websocket"],
      withCredentials: false,
      auth: { token, userId }, // ✅ userId UUID obligatoire (room = UUID côté backend)
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socketLog("[RDVSocket] ✅ connected", socket.id);

      // ping test
      socket.emit("ping", { ts: Date.now() });
    });

    socket.on("connect_error", (e) => {
      socketLog("[RDVSocket] ❌ connect_error", e?.message, e);
    });

    socket.on("ws_ready", (d) => {
      socketLog("[RDVSocket] ✅ ws_ready", d);
    });

    // debug room join from gateway
    socket.on("debug_room", (d) => {
      socketLog("[RDVSocket] 🧪 debug_room", d);
    });

    socket.on("pong", (d) => {
      socketLog("[RDVSocket] 🟢 pong", d);
    });

    // ✅ event envoyé par RDVSocketGateway
    socket.on("rendezvous_reminder", (payload) => {
      socketLog("[RDVSocket] 🔔 rendezvous_reminder", payload);

      const rdvId = payload?.rdvId ?? payload?.id ?? payload?.rendezvousId;

      dispatch(
        setRendezvousReminder({
          ...payload,
          rdvId,
          _key: `${rdvId || "no-id"}:${payload?.startTime || Date.now()}`,
        })
      );
    });

    socket.on("disconnect", (reason) => {
      socketLog("[RDVSocket] ❌ disconnected", reason);
    });

    return () => {
      try {
        socket.off();
        socket.disconnect();
      } catch {}
      socketRef.current = null;
    };
  }, [dispatch, token, userId]);

  // helper pour marquer vu via socket
  const markSeen = (rdvId) => {
    const s = socketRef.current;
    socketLog("[RDVSocket] markSeen", { rdvId, hasSocket: !!s });
    if (!s || !rdvId) return;
    s.emit("confirm_seen", { rdvId });
  };

  return { markSeen, socket: socketRef.current };
}
